// frontend/src/components/dashboard/FotographiyaSlideBar.jsx
import React, { useEffect } from "react";
import "../../styles/dashboard/UserMarketing.css";
import {
  Gem,
  Heart,
  Compass,
  Building2,
  PartyPopper,
  Sparkles,
  Baby,
  GraduationCap,
  CalendarCheck,
  CheckCircle2,
  Star,
  Globe,
  ExternalLink,
  ChevronRight,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  FaInstagram,
  FaYoutube,
  FaFacebook,
  FaLinkedinIn,
  FaGoogle,
} from "react-icons/fa";

const SERVICES_LIST = [
  {
    id: "wedding",
    IconComponent: Gem,
    iconColor: "#e11d48",
    bgColor: "#ffe4e6",
    title: "Wedding Photography",
    tag: "Royal & Candid",
    url: "https://www.fotographiya.com/best-wedding-photographer",
  },
  {
    id: "pre-wedding",
    IconComponent: Heart,
    iconColor: "#db2777",
    bgColor: "#fce7f3",
    title: "Pre-Wedding Shoots",
    tag: "Cinematic Romance",
    url: "https://www.fotographiya.com/best-pre-wedding-photographer",
  },
  {
    id: "destination",
    IconComponent: Compass,
    iconColor: "#d97706",
    bgColor: "#fef3c7",
    title: "Destination Weddings",
    tag: "Palaces & Forts",
    url: "https://www.fotographiya.com/destination-wedding-photographer",
  },
  {
    id: "corporate",
    IconComponent: Building2,
    iconColor: "#2563eb",
    bgColor: "#dbeafe",
    title: "Corporate & Expos",
    tag: "Summits & Marathons",
    url: "https://www.fotographiya.com/corporate-event-photographer",
  },
  {
    id: "birthday",
    IconComponent: PartyPopper,
    iconColor: "#ea580c",
    bgColor: "#ffedd5",
    title: "Birthday & Milestones",
    tag: "Joyful Moments",
    url: "https://www.fotographiya.com/birthday-party-photographer",
  },
  {
    id: "roka",
    IconComponent: Sparkles,
    iconColor: "#9333ea",
    bgColor: "#f3e8ff",
    title: "Roka & Engagements",
    tag: "Family Traditions",
    url: "https://www.fotographiya.com/roka-ceremony-photographer",
  },
  {
    id: "maternity",
    IconComponent: Baby,
    iconColor: "#0284c7",
    bgColor: "#e0f2fe",
    title: "Maternity Shoots",
    tag: "Pure Grace",
    url: "https://www.fotographiya.com/maternity-photographer",
  },
  {
    id: "academy",
    IconComponent: GraduationCap,
    iconColor: "#4f46e5",
    bgColor: "#e0e7ff",
    title: "Photography Academy",
    tag: "Learn Pro Skills",
    url: "https://www.fotographiya.com/photography-academy",
  },
];

const FotographiyaSlideBar = ({ isOpen, onClose }) => {
  // Close drawer on Escape key and prevent background scroll
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div
      className={`foto-slidebar-overlay ${isOpen ? "open" : ""}`}
      onClick={onClose}
      aria-hidden={!isOpen}
    >
      <aside
        className="foto-slidebar-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Fotographiya Slide Bar"
      >
        {/* Drawer Header */}
        <div className="foto-drawer-header">
          <div className="foto-drawer-brand">
            <img
              src="/images/headerlogo.png"
              alt="Fotographiya"
              className="foto-drawer-logo"
            />
            <span
              className="foto-drawer-badge"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <ShieldCheck size={12} strokeWidth={2.4} />
              <span>Official Hub</span>
            </span>
          </div>
          <button
            type="button"
            className="foto-drawer-close-btn"
            onClick={onClose}
            aria-label="Close Slide Bar"
            title="Close"
          >
            <X size={18} strokeWidth={2.2} />
          </button>
        </div>

        {/* Drawer Scrollable Body */}
        <div className="foto-drawer-body">
          {/* ⚡ 1. Prime Booking & Reservation Card (Sabse Upar) */}
          <div className="foto-drawer-cta-card">
            <div className="foto-drawer-cta-top">
              <span
                className="foto-drawer-cta-badge"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <CalendarCheck size={13} strokeWidth={2.2} />
                <span>Reserve Your Date</span>
              </span>
              <ExternalLink size={15} style={{ opacity: 0.85 }} />
            </div>
            <h4 className="foto-drawer-cta-title">Planning a Wedding or Grand Event?</h4>
            <p className="foto-drawer-cta-desc">
              Book Fotographiya for your special date. Get high-end photography combined with instant AI face delivery for all your guests!
            </p>
            <a
              href="https://www.fotographiya.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="foto-drawer-cta-btn"
              title="Book Photography Shoot"
            >
              <span>Book Shoot / Inquire Now</span>
              <ExternalLink size={13} strokeWidth={2.2} />
            </a>
          </div>

          {/* 👑 2. Founder Spotlight Card */}
          <div className="foto-drawer-founder-card">
            <img
              src="/images/login-banner2.jpg"
              alt="Mohit Barthunia - Founder of Fotographiya"
              className="foto-drawer-founder-img"
              loading="lazy"
            />
            <div className="foto-drawer-founder-info">
              <h4
                className="foto-drawer-founder-name"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span>Mohit Barthunia</span>
                <CheckCircle2
                  size={15}
                  color="#7c3aed"
                  strokeWidth={2.4}
                  title="Verified Founder"
                />
              </h4>
              <span className="foto-drawer-founder-role">Founder • Fotographiya</span>
              <p className="foto-drawer-founder-quote">
                “Crafting timeless memories with passion and elevating celebration experiences with cutting-edge AI photo delivery.”
              </p>
            </div>
          </div>

          {/* 🚀 3. Social Media & Reviews Channels */}
          <div>
            <h4 className="foto-drawer-section-title">
              <span>Connect & Follow</span>
            </h4>
            <div className="foto-drawer-social-grid">
              {/* Instagram */}
              <a
                href="https://www.instagram.com/fotographiya_official/"
                target="_blank"
                rel="noopener noreferrer"
                className="foto-drawer-social-item item-instagram"
                title="Follow on Instagram"
              >
                <div className="drawer-social-icon">
                  <FaInstagram />
                </div>
                <div className="drawer-social-text">
                  <span className="drawer-social-title">Instagram</span>
                  <span className="drawer-social-sub">@fotographiya_official</span>
                </div>
              </a>

              {/* YouTube */}
              <a
                href="https://www.youtube.com/@Fotographiya_official"
                target="_blank"
                rel="noopener noreferrer"
                className="foto-drawer-social-item item-youtube"
                title="Subscribe on YouTube"
              >
                <div className="drawer-social-icon">
                  <FaYoutube />
                </div>
                <div className="drawer-social-text">
                  <span className="drawer-social-title">YouTube</span>
                  <span className="drawer-social-sub">@Fotographiya_official</span>
                </div>
              </a>

              {/* Facebook */}
              <a
                href="https://www.facebook.com/people/Fotographiya-Wedphotography/100092454265642/"
                target="_blank"
                rel="noopener noreferrer"
                className="foto-drawer-social-item item-facebook"
                title="Like on Facebook"
              >
                <div className="drawer-social-icon">
                  <FaFacebook />
                </div>
                <div className="drawer-social-text">
                  <span className="drawer-social-title">Facebook</span>
                  <span className="drawer-social-sub">Fotographiya Page</span>
                </div>
              </a>

              {/* LinkedIn */}
              <a
                href="https://in.linkedin.com/in/mohit-barthunia"
                target="_blank"
                rel="noopener noreferrer"
                className="foto-drawer-social-item item-linkedin"
                title="Connect on LinkedIn"
              >
                <div className="drawer-social-icon">
                  <FaLinkedinIn />
                </div>
                <div className="drawer-social-text">
                  <span className="drawer-social-title">LinkedIn</span>
                  <span className="drawer-social-sub">Mohit Barthunia</span>
                </div>
              </a>

              {/* Google Reviews */}
              <a
                href="https://g.page/r/CZRiMBryiw02EAI/review"
                target="_blank"
                rel="noopener noreferrer"
                className="foto-drawer-social-item item-google"
                title="Rate us 5 Stars on Google"
              >
                <div className="drawer-social-icon">
                  <FaGoogle />
                </div>
                <div className="drawer-social-text">
                  <span
                    className="drawer-social-title"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <Star
                      size={13}
                      fill="#f59e0b"
                      color="#f59e0b"
                      strokeWidth={1}
                    />
                    <span>4.9 Verified Google Reviews</span>
                  </span>
                  <span className="drawer-social-sub">
                    Rate your experience with Fotographiya
                  </span>
                </div>
              </a>
            </div>
          </div>

          {/* 📸 4. Services Directory */}
          <div>
            <h4 className="foto-drawer-section-title">
              <span>Photography Services</span>
            </h4>
            <div className="foto-drawer-services-list">
              {SERVICES_LIST.map((svc) => (
                <a
                  key={svc.id}
                  href={svc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="foto-drawer-service-link"
                  title={`View ${svc.title}`}
                >
                  <div className="drawer-svc-left">
                    <div
                      className="drawer-svc-icon"
                      style={{
                        backgroundColor: svc.bgColor,
                        color: svc.iconColor,
                      }}
                    >
                      <svc.IconComponent size={17} strokeWidth={2.2} />
                    </div>
                    <div>
                      <div className="drawer-svc-title">{svc.title}</div>
                      <div className="drawer-svc-tag">{svc.tag}</div>
                    </div>
                  </div>
                  <span className="drawer-svc-arrow">
                    <ChevronRight size={16} />
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* 🌐 5. Featured Website Action Card (Sabse Niche) */}
          <div className="foto-drawer-website-card">
            <div className="foto-drawer-website-top">
              <span
                className="foto-drawer-site-tag"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <Globe size={12} strokeWidth={2.2} />
                <span>Official Portal</span>
              </span>
              <ExternalLink size={16} />
            </div>
            <h3 className="foto-drawer-website-title">fotographiya.com</h3>
            <p className="foto-drawer-website-desc">
              Explore luxury wedding cinema, complete client galleries, pricing packages, and award-winning stories across India.
            </p>
            <a
              href="https://www.fotographiya.com"
              target="_blank"
              rel="noopener noreferrer"
              className="foto-drawer-site-btn"
              title="Visit Fotographiya Official Website"
            >
              <Globe size={14} strokeWidth={2.2} />
              <span>Visit Official Website</span>
              <ExternalLink size={13} strokeWidth={2.2} />
            </a>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default FotographiyaSlideBar;
